import React, { FC, useCallback } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import type { ImmutableTrip } from 'src/types/trips/Trips';
import type { Offer } from 'src/types/offer/Offer';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import { FORMS, TIME_FIELDS } from '../../../constants';
import { modalMapDispatchToProps } from '../../../submission';
import { clearFields, patchOffer } from '../../../ducks/offer';

import TimeField from '../../../../common/components/fields/TimeField';
import { MOMENT_FORMAT } from '../../../../common/constants';
import DateTime from '../../DateTime';
import ClearButton from '../../../../common/components/ClearButton';
import FormButtons from '../../../../common/components/FormButtons';
import s from './TimeForm.module.scss';

export const ERROR_MESSAGES = {
  RETURN_TIME_THE_SAME: 'Return time can\'t be the same as depart time!',
  RETURN_TIME_IS_BEFORE: 'Return time can\'t be before the depart time!',
  MIN_TRIP_1_HOUR: 'Return time must be at least 1 hour after depart time.',
};

type TimeFormValues = Partial<Pick<Offer, 'pickup_time' | 'return_time'>>;

type BareTimeFormProps = {
  initialValues: TimeFormValues;
  trip: ImmutableTrip;
};

export const BareTimeForm: FC<BareTimeFormProps> = ({
  initialValues,
  trip,
}) => {
  const dispatch = useDispatch();
  const { closeModal } = modalMapDispatchToProps(FORMS.TIME)(dispatch);
  const { offerTrip: { sameDates, isOfferTimesModified } } = useSelector(dateTimeDurationSelector(trip.get('pk')));

  const validate = useCallback((values: TimeFormValues) => {
    const errors: Partial<TimeFormValues> = {};

    if (!values.pickup_time) {
      errors.pickup_time = 'Please enter depart time';
    }
    if (!values.return_time) {
      errors.return_time = 'Please enter return time';
    }

    const departTimeMoment = moment(values.pickup_time, MOMENT_FORMAT.TIME_24);
    const returnTimeMoment = moment(values.return_time, MOMENT_FORMAT.TIME_24);

    if (sameDates) {
      if (moment.duration(returnTimeMoment.diff(departTimeMoment)).asHours() < 1) {
        errors.return_time = ERROR_MESSAGES.MIN_TRIP_1_HOUR;
      }

      if (returnTimeMoment.isSame(departTimeMoment)) {
        errors.return_time = ERROR_MESSAGES.RETURN_TIME_THE_SAME;
      }
      if (returnTimeMoment.isBefore(departTimeMoment)) {
        errors.return_time = ERROR_MESSAGES.RETURN_TIME_IS_BEFORE;
      }
    }

    return errors;
  }, [sameDates]);

  return (
    <Form<TimeFormValues>
      validate={validate}
      onSubmit={(values) => {
        dispatch(
          patchOffer({
            pickup_time: values.pickup_time,
            return_time: values.return_time,
          }),
        );
        closeModal();
      }}
      initialValues={initialValues}
    >
      {({ handleSubmit, dirty, form: { reset } }) => (
        <form
          method="POST"
          onSubmit={handleSubmit}
          className={s.root}
        >
          <h3 className={s.title}>
            Edit Start & End Time
          </h3>
          <div className={s.fieldset}>
            <ClearButton
              disabled={!(dirty || isOfferTimesModified)}
              onClick={() => {
                reset();
                dispatch(clearFields(TIME_FIELDS));
                closeModal();
              }}
            />
            <DateTime
              classNameModifier="timeForm"
              departHeading="Start"
              returnHeading="End"
              formatString={MOMENT_FORMAT.TIME}
              departDateTime={(
                <TimeField
                  name={'pickup_time' as keyof TimeFormValues}
                />
              )}
              returnDateTime={(
                <TimeField
                  autoFocus={null}
                  valid
                  name={'return_time' as keyof TimeFormValues}
                />
              )}
            />
          </div>
          <FormButtons
            classNameModifier="inbox"
            closeModal={closeModal}
          />
        </form>
      )}
    </Form>
  );
};

export default BareTimeForm;
